import { Button, PageError, PageHeader, PageLoading } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { messageFor } from "../../error-copy";
import {
  codeFor,
  REASON_SCOPE,
  REASON_SCOPES,
  type ReasonScope,
  reasonActions,
  reasonsQuery,
  type StatusReason,
} from "../../reasons";
import { ConfirmDialog } from "../shared/confirm";
import styles from "./settings.module.css";

/** Every reason list in the console reloads after a change. */
const refreshReasons = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: ["status-reasons"] });

/**
 * Status reasons (P15-14, Settings › Operations): the reasons staff pick from when they cancel an
 * order, or cancel, suspend or reroute a delivery. Reports count by them. A retired reason can't be
 * picked any more; past records keep it.
 */
export function StatusReasonsPage(): ReactNode {
  const queryClient = useQueryClient();
  const reasons = useQuery(reasonsQuery(undefined, true));
  const [adding, setAdding] = useState<ReasonScope | null>(null);
  const [renaming, setRenaming] = useState<StatusReason | null>(null);
  const [retiring, setRetiring] = useState<StatusReason | null>(null);
  const restore = useMutation({
    mutationFn: (r: StatusReason) => reasonActions.update(r.id, { is_active: true }),
    onSuccess: () => refreshReasons(queryClient),
  });

  const header = (
    <PageHeader
      title="Status reasons"
      description="What staff pick when they cancel an order, or cancel, suspend or reroute a delivery. Reports count by them."
    />
  );
  if (reasons.status === "pending")
    return (
      <>
        {header}
        <PageLoading label="Loading the reasons" />
      </>
    );
  if (reasons.status === "error")
    return (
      <>
        {header}
        <PageError message={messageFor(errorOf(reasons.error))} onRetry={() => reasons.refetch()} />
      </>
    );
  return (
    <>
      {header}
      {restore.error ? (
        <Alert tone="danger" title="Not restored">
          {messageFor(errorOf(restore.error))}
        </Alert>
      ) : null}
      {REASON_SCOPES.map((scope) => (
        <section key={scope} className={styles.panel} aria-label={REASON_SCOPE[scope].label}>
          <div className={styles.toolbar}>
            <h2 className={styles.sectionTitle}>{REASON_SCOPE[scope].label}</h2>
            <Button variant="secondary" size="sm" onClick={() => setAdding(scope)}>
              Add a reason
            </Button>
          </div>
          <p className={styles.muted}>{REASON_SCOPE[scope].help}</p>
          <SortableDataTable
            caption={`Reasons for ${REASON_SCOPE[scope].label.toLowerCase()}`}
            rowKey="id"
            rows={reasons.data.filter((r) => r.applies_to === scope)}
            itemName={{ one: "reason", other: "reasons" }}
            emptyMessage="No reasons yet: staff write one in their own words. Add some to count them in reports."
            columns={[
              { key: "name", label: "Reason", sortable: false },
              {
                key: "is_active",
                label: "Status",
                sortable: false,
                render: (_v, r) =>
                  r.is_active ? <Badge tone="success">In use</Badge> : <Badge tone="neutral">Retired</Badge>,
              },
              {
                key: "actions",
                label: "",
                sortable: false,
                render: (_v, r) => (
                  <div className={styles.actions}>
                    <Button variant="ghost" size="sm" onClick={() => setRenaming(r)}>
                      Rename
                    </Button>
                    {r.is_active ? (
                      <Button variant="ghost" size="sm" onClick={() => setRetiring(r)}>
                        Retire
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={restore.isPending && restore.variables?.id === r.id}
                        onClick={() => restore.mutate(r)}
                      >
                        Restore
                      </Button>
                    )}
                  </div>
                ),
              },
            ]}
          />
        </section>
      ))}
      {adding ? <ReasonDialog scope={adding} onClose={() => setAdding(null)} /> : null}
      {renaming ? <ReasonDialog reason={renaming} onClose={() => setRenaming(null)} /> : null}
      {retiring ? (
        <ConfirmDialog
          title={`Retire ${retiring.name}?`}
          description="Staff can't pick it any more. Past orders and deliveries keep it, and you can restore it later."
          action="Retire"
          run={() => reasonActions.update(retiring.id, { is_active: false })}
          onDone={() => refreshReasons(queryClient)}
          onClose={() => setRetiring(null)}
        />
      ) : null}
    </>
  );
}

/** Add a reason to a list, or rename one. A new reason's code comes from its name. */
function ReasonDialog({ scope, reason, onClose }: { scope?: ReasonScope; reason?: StatusReason; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(reason?.name ?? "");
  const mutation = useMutation({
    mutationFn: () =>
      reason
        ? reasonActions.update(reason.id, { name: name.trim() })
        : reasonActions.create({ applies_to: scope as ReasonScope, code: codeFor(name), name: name.trim() }),
    onSuccess: async () => {
      await refreshReasons(queryClient);
      onClose();
    },
  });
  const list = REASON_SCOPE[reason?.applies_to ?? (scope as ReasonScope)].label.toLowerCase();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={reason ? `Rename ${reason.name}` : `Add a reason for ${list}`}>
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) mutation.mutate();
          }}
        >
          <Input
            label="Reason"
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            placeholder="The gate was locked"
            description={reason ? "Past records show the new name too." : undefined}
          />
          {mutation.error ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim()} loading={mutation.isPending}>
              {reason ? "Save" : "Add reason"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
